import { distance } from '@turf/distance';

import {
  AircraftEvent,
  AircraftEventAirport,
  MaoLocation,
  MaoMaintenance,
  MaoTrip,
  MaoTripLeg,
} from '#entities';

import { EAircraftEventType } from '../../shared';

import { getAirportLabel } from './getAirportLabel';
import { getPartOfMap } from './getPartOfMap';
import { parseDonorReferencePrimary } from './parseDonorReference';

const getTripLegEvent = (
  leg: MaoTripLeg,
  trip: Pick<MaoTrip, 'ExternalReference'>,
  location: MaoLocation,
): AircraftEvent => {
  const { organ, unosId, organShort } = parseDonorReferencePrimary(
    trip.ExternalReference,
  );
  const now = Date.now();
  const startUnix = leg.OffBlocksUtcUnix;
  const endUnix = leg.OnBlocksUtcUnix;

  const airports: AircraftEventAirport[] = [
    leg.DepartureAirport,
    leg.ArrivalAirport,
  ].map(airport => ({
    id: airport.Id,
    iata: airport.IATA,
    icao: airport.ICAO,
    label: getAirportLabel(airport.IATA, airport.ICAO),
    startUnix,
    endUnix,
    partOfMap: getPartOfMap([airport.Longitude, airport.Latitude]),
    distance: distance(
      [airport.Longitude, airport.Latitude],
      [location.Longitude, location.Latitude],
      { units: 'meters' },
    ),
    airport,
    ianaTimezoneEnd: airport.IanaTimeZone,
    ianaTimezoneStart: airport.IanaTimeZone,
    eventType: EAircraftEventType.TripLeg,
    eventName: `${
      unosId ? `${unosId}${organShort}` : trip.ExternalReference
    } leg-${leg.SectorIndex}`,
  }));

  // Handle test flights with the same departure and arrival airport
  if (airports.length === 2 && airports[0].iata === airports[1].iata) {
    airports.pop();
  }
  return {
    id: `trip-leg-${leg.Id}`,
    type: EAircraftEventType.TripLeg,
    organ: organ ?? null,
    label: `${unosId ? `${unosId}${organShort}` : trip.ExternalReference}`,
    range: [startUnix, endUnix],
    isStarted: leg.Movement?.IsOffBlocks ?? startUnix < now,
    isEnded: leg.Movement?.IsOnBlocks ?? endUnix < now,
    startUnix,
    endUnix,
    startIanaTimezone: airports[0]?.ianaTimezoneStart,
    endIanaTimezone: airports[1]?.ianaTimezoneEnd,
    airports,
    maintenanceType: null,
    sectorIndex: leg.SectorIndex,
  };
};

const getTripEvent = (trip: MaoTrip, location: MaoLocation): AircraftEvent => {
  const { organ, unosId, organShort } = parseDonorReferencePrimary(
    trip.ExternalReference,
  );
  const now = Date.now();
  const startUnix = trip.TripStartUnix;
  const endUnix = trip.TripEndUnix;
  const airports = trip.Legs.toSorted(
    (a, b) => a.OffBlocksUtcUnix - b.OffBlocksUtcUnix,
  ).reduce<AircraftEventAirport[]>((acc, leg, index, legs) => {
    const startTimezone = leg.DepartureAirport.IanaTimeZone;
    const endTimezone = leg.ArrivalAirport.IanaTimeZone;
    acc.push({
      id: leg.Id,
      iata: leg.DepartureAirport.IATA,
      icao: leg.DepartureAirport.ICAO,
      label: getAirportLabel(
        leg.DepartureAirport.IATA,
        leg.DepartureAirport.ICAO,
      ),
      startUnix:
        (index && legs[index - 1].OnBlocksUtcUnix) || leg.OffBlocksUtcUnix,
      endUnix: leg.OffBlocksUtcUnix,
      ianaTimezoneStart: startTimezone,
      ianaTimezoneEnd: endTimezone,
      partOfMap: getPartOfMap([
        leg.DepartureAirport.Longitude,
        leg.DepartureAirport.Latitude,
      ]),
      distance: distance(
        [leg.DepartureAirport.Longitude, leg.DepartureAirport.Latitude],
        [location.Longitude, location.Latitude],
        { units: 'meters' },
      ),
      airport: leg.DepartureAirport,
      eventType: EAircraftEventType.Trip,
      eventName: unosId ? `${unosId}${organShort}` : trip.ExternalReference,
    });

    if (index === legs.length - 1) {
      acc.push({
        id: leg.Id,
        iata: leg.ArrivalAirport.IATA,
        icao: leg.ArrivalAirport.ICAO,
        label: getAirportLabel(
          leg.ArrivalAirport.IATA,
          leg.ArrivalAirport.ICAO,
        ),
        startUnix: leg.OnBlocksUtcUnix,
        endUnix: leg.OnBlocksUtcUnix,
        ianaTimezoneStart: startTimezone,
        ianaTimezoneEnd: endTimezone,
        partOfMap: getPartOfMap([
          leg.ArrivalAirport.Longitude,
          leg.ArrivalAirport.Latitude,
        ]),
        distance: distance(
          [leg.ArrivalAirport.Longitude, leg.ArrivalAirport.Latitude],
          [location.Longitude, location.Latitude],
          { units: 'meters' },
        ),
        airport: leg.ArrivalAirport,
        eventType: EAircraftEventType.Trip,
        eventName: unosId ? `${unosId}${organShort}` : trip.ExternalReference,
      });
    }
    return acc;
  }, []);

  // Handle test flights with the same departure and arrival airport
  if (airports.length === 2 && airports[0].iata === airports[1].iata) {
    airports.pop();
  }
  return {
    id: `trip-${trip.Id}`,
    type: EAircraftEventType.Trip,
    organ: organ ?? null,
    label: unosId ? `${unosId}${organShort}` : trip.ExternalReference,
    range: [startUnix, endUnix],
    isStarted: startUnix < now,
    isEnded: endUnix < now,
    children: trip.Legs.map(leg =>
      getTripLegEvent(leg, trip, location),
    ).toSorted((a, b) => a.range[0] - b.range[0]),
    startUnix,
    startIanaTimezone: airports[0]?.ianaTimezoneStart,
    endUnix,
    endIanaTimezone: airports[0]?.ianaTimezoneEnd,
    airports,
    maintenanceType: null,
  };
};

export const getAircraftEvents = (
  location: MaoLocation,
  maintenances: MaoMaintenance[],
  trips: MaoTrip[],
) => {
  const eventList: AircraftEvent[] = [];
  const now = Date.now();
  eventList.push(
    ...(trips?.map(trip => getTripEvent(trip, location)) || []),
    ...maintenances.map((m): AircraftEvent => {
      const timezone = m.Airport.IanaTimeZone;
      const startUnix =
        m.ActualStartDateAndTimeUtcUnix || m.ScheduledStartDateAndTimeUtcUnix;
      const endUnix =
        m.ActualEndDateAndTimeUtcUnix || m.ScheduledEndDateAndTimeUtcUnix;
      const airport: AircraftEventAirport = {
        id: m.Id,
        iata: m.Airport.IATA,
        icao: m.Airport.ICAO,
        label: getAirportLabel(m.Airport.IATA, m.Airport.ICAO),
        startUnix,
        endUnix,
        ianaTimezoneStart: timezone,
        ianaTimezoneEnd: timezone,
        partOfMap: getPartOfMap([m.Airport.Longitude, m.Airport.Latitude]),
        distance: distance(
          [m.Airport.Longitude, m.Airport.Latitude],
          [location.Longitude, location.Latitude],
          { units: 'meters' },
        ),
        airport: m.Airport,
        eventType: EAircraftEventType.Maintenance,
        eventName: `Maint. ${m.MaintenanceType.OpsBoardBadge}`,
      };
      return {
        id: `maintenance-${m.Id}`,
        label: `Maint. ${m.MaintenanceType.OpsBoardBadge}`,
        type: EAircraftEventType.Maintenance,
        range: [startUnix, endUnix],
        isStarted: startUnix < now,
        isEnded: endUnix < now,
        organ: null,
        startUnix,
        startIanaTimezone: timezone,
        endUnix,
        endIanaTimezone: timezone,
        airports: [airport],
        maintenanceType: m.MaintenanceType,
      };
    }),
  );
  return eventList.toSorted((a, b) => a.range[0] - b.range[0]);
};
